import React, { useState, useEffect, useContext, useMemo } from 'react'
import { Polygon, MarkerClusterer, useGoogleMap } from '@react-google-maps/api';
import DataCacheContext from '../context/DataCacheContext';
import LayersContext from '../context/LayersContext';
import SearchContext from '../context/SearchContext';
import { applyAllFilters } from '../util/SearchUtil';
import { getArrayDifference } from '../util/ArrayUtil';

const validLatLng = (icon) => {
  if (!icon.iconPoint) return false;
  return icon.iconPoint.latitude && icon.iconPoint.longitude && !isNaN(icon.iconPoint.latitude) && !isNaN(icon.iconPoint.longitude);
}

const latlng = (icon) => {
  return { lat: icon.iconPoint.latitude, lng: icon.iconPoint.longitude }
}

/*const getPixelPositionOffset = (offsetWidth, offsetHeight, icon) => {
  var labelAnchor = { x: 10, y: -52 };
  if (icon.rotation > 45 && icon.rotation <= 135) {
    labelAnchor = { x: -40, y: -52 };
  }
  return {
    x: offsetWidth + labelAnchor.x,
    y: offsetHeight + labelAnchor.y,
  };
};

const MapPolyline = (props) => {
  var icon = props.icon;
  const options = {
    strokeColor: icon.congestionColor,
    strokeOpacity: 1,
    strokeWeight: 1,
    clickable: true,
    draggable: false,
    editable: false,
    visible: true,
    paths: icon.polyline,
    zIndex: 1
  }
  return (
    <Polyline
      path={icon.polyline}
      options={options}
    />
  )
}*/

const MapPolygon = (props) => {
  var icon = props.icon;
  const options = {
    strokeColor: "#000000",
    strokeOpacity: 0.5,
    strokeWeight: 2,
    clickable: true,
    draggable: false,
    editable: false,
    visible: true,
    fillColor: icon.congestionColor,
    fillOpacity: 0.75,
  }
  return (
    <Polygon paths={icon.polygon} options={options} />
  )
}

const MapMarker = (props) => {
  const icon = props.icon;
  var pos = latlng(icon);
  const MyMarker = props.module.marker;
  return (
    <MyMarker {...props} position={pos} />
  )
}

const getQueryJson = async (endpoint, setJson, queryStr) => {
  const res = await fetch(process.env.REACT_APP_API_HOST + "/" + endpoint + queryStr, {
      method: 'GET',
      headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
      },
  });
  res.json().then(res => {
      setJson(res);
  })
}

export default function EndpointIconLayer(props) {
  const layersContext = React.useContext(LayersContext);
  const searchContext = useContext(SearchContext);
  const module = props.module;
  const title = module.name;
  const [allObjects, setAllObjects] = useState([]);
  const [objects, setObjects] = useState([]);
  const [visible, setVisible] = useState(module.defaultOn);
  const endpoint = module.endpoint;
  const [query, setQuery] = useState(null);
  const dataCache = useContext(DataCacheContext);
  const updateDelay = module.updateDelay;
  const selectedMarker = props.selectedMarker;
  const setSelectedMarker = props.setSelectedMarker;
  const setLayersOn = props.setLayersOn;
  const setRefreshWms = props.setRefreshWms;
  const currentZoom = props.currentZoom;
  const resizeOnZoom = module.resizeOnZoom;
  const [lastZoom, setLastZoom] = React.useState(currentZoom);
  const getQuery = module.getQuery;

  useEffect(() => {
    if (visible) {
      setLayersOn(layersContext => {
        let newLayersContext = { ...layersContext };
        if(!newLayersContext[title]) {
          newLayersContext[title] = {enabled:true}
        } else {
          newLayersContext[title].enabled = true;
        }
        return newLayersContext;
      })
    }
  }, [])

  // useCallback to prevent duplicate listener function references
  const setObjectsListener = React.useCallback((e) => {
    setAllObjects(prevObjects => {
      const removed = getArrayDifference(prevObjects, e.data).filter(obj => obj.geometry);
      setRefreshWms(removed.length > 0);
      return e.data;
    });
  }, [setRefreshWms]);

  useEffect(() => {
    if (endpoint && dataCache && !isNaN(updateDelay) && query === null) {
      console.log("creating datacache for endpoint: " + endpoint);
      dataCache.createDataCache(endpoint, updateDelay, setObjectsListener);
    } 
    if(null !== query && dataCache) {
      dataCache.data[endpoint].postMessage({ msg: "pause" });
      getQueryJson(endpoint, setAllObjects, query);
    }
    
    // pause worker updates when component unmounts
    return () => {
      if (endpoint && dataCache?.data?.[endpoint]) {
        dataCache.data[endpoint].postMessage({ msg: "pause" });
      }
    };
  }, [endpoint, dataCache, updateDelay, query, setObjectsListener]);

  useEffect(() => {
    if (undefined !== layersContext[title]) {
      const isSelected = layersContext[title].enabled;
      setVisible(isSelected);
      if (!isSelected) {
        if (selectedMarker) {
          var found = objects.some(obj => selectedMarker === obj.id);
          if (found) {
            setSelectedMarker(null);
          }
        }
      }
      if (getQuery && layersContext[title]?.query) {
        setQuery(getQuery(layersContext[title]?.query));
      } else {
        setQuery(null);
      }
    }
  }, [layersContext, title]);

  useEffect(() => {
    applyAllFilters(allObjects, searchContext.searchString, setObjects);
  }, [allObjects, searchContext.searchString])
  
  useEffect(() => {
    if (resizeOnZoom) {
      if ((currentZoom === 7 && lastZoom === 8) || (currentZoom === 8 && lastZoom === 7)) {
        setObjects(objects => { return [...objects] });
      } else if ((currentZoom === 11 && lastZoom === 10) || (currentZoom === 10 && lastZoom === 11)) {
        setObjects(objects => { return [...objects] });
      }
      setLastZoom(currentZoom);
    }
  }, [currentZoom, resizeOnZoom, lastZoom])

  const endPointLayer = useMemo(() => <Layer objects={objects} {...props} module={module} />, [objects]);

  if (objects.length === 0 || !visible) {
    return null;
  } else {
    return (
      <>
        {endPointLayer}
      </>
    )
  }
}

const Layer = (props) => {
  const objects = props.objects;
  const module = props.module;
  const map = useGoogleMap();

  const markers = objects.map(obj => {
    if (validLatLng(obj)) return <MapMarker {...props} icon={obj} key={obj.id + "mapmarker"} />;
    else if (obj.polygon) return <MapPolygon icon={obj} key={obj.id + "mappolygon"} />
    return null;
  })

  if (module.cluster) {
    const options = {
      imagePath: module.clusterMarker,
      imageExtension: "png",
      imageSizes: [30, 35, 40, 45, 50, 55],
      zoomOnClick: true
    }
    if (module.clusterStyles) {
      options.styles = module.clusterStyles
    }
    const attachClusterClickHandlers = (clusterer) => {
      window.setTimeout(() => {
        clusterer.getClusters().forEach(cluster => {
          const clusterElement = cluster.clusterIcon?.div;
          if (!clusterElement || clusterElement.dataset.zoomOnClickAttached) return;

          clusterElement.dataset.zoomOnClickAttached = "true";
          clusterElement.addEventListener("click", () => {
            if (map) map.fitBounds(cluster.getBounds());
          });
        });
      }, 0);
    }
    return (
      <MarkerClusterer options={options} clusterClass={module.clusterClass} maxZoom={13} onClusteringEnd={attachClusterClickHandlers}>
        {(clusterer) => objects.map((obj) => (
          <MapMarker {...props} icon={obj} key={obj.id + "mapmarker"} clusterer={clusterer} />
        ))
        }
      </MarkerClusterer>
    )
  } else {
    return (
      markers
    )
  }
}